import {
  ApiRoutes,
  Mensagens,
  MotivoContato,
  TipoPessoa,
  TIPOS_PESSOA,
} from "../../models/types";
import {
  formatarDinheiro,
  getBaseURL,
  getDescricaoAdicionalProtocolo,
  getMatriculaResponsavelProtocolo,
  getMatriculaSolicitante,
  getNumeroDDD,
  getNumeroSemDDD,
  getOnlyNumbers,
  isApenasNumeros,
  isCnpjValido,
  isCpfValido,
} from "../../utils/functions";
import CardReCaptcha, {
  CardReCaptchaRef,
} from "../../components/form/CardReCaptcha";
import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useIonLoading } from "@ionic/react";
import Page from "../../components/Page";
import Accordion from "../../components/Accordion";
import useNotification from "../../hooks/useNotification";
import ReactForm from "../../components/form/ReactForm";
import SubmitButton from "../../components/form/SubmitButton";
import ButtonBack from "../../components/ButtonBack";
import DivFaltaAgua from "../../components/DivFaltaAgua";
import InputComplete from "../../components/form/InputComplete";
import InputText from "../../components/form/InputText";
import axios from "axios";
import SelectField from "../../components/form/SelectField";

export default function SolicitarReligacaoAguaNoEndereco() {
  const reCaptchaRef = React.createRef<CardReCaptchaRef>();
  const notification = useNotification();

  const [present, dismiss] = useIonLoading();
  const [isFluxoCadastro, setIsFluxoCadastro] = useState<boolean>(false);
  const [listaTaxa, setListaTaxa] = useState<any>();
  const [tipoCliente, setTipoCliente] = useState<string>();
  const [numeroConta, setNumeroConta] = useState("");
  const [digitoConta, setDigitoConta] = useState("");

  const isTipoClientePessoaFisica = (() => {
    return tipoCliente === TipoPessoa.PESSOA_FISICA;
  })();

  const handleChangeNumeroConta = (e: any) => {
    if (e.target.value === "" || isApenasNumeros(e.target.value)) {
      setNumeroConta(e.target.value);
    }
  };

  const handleChangeDigitoConta = (e: any) => {
    if (e.target.value === "" || isApenasNumeros(e.target.value)) {
      setDigitoConta(e.target.value);
    }
  };

  useEffect(() => {
    setIsFluxoCadastro(false);
  }, []);

  const formSchemaConsultaConta = z.object({
    numeroConta: z
      .string()
      .min(1, "Informe o número da conta.")
      .transform((val) => getOnlyNumbers(val)),
    digitoConta: z
      .string()
      .min(1, "Informe o dígito da conta.")
      .transform((val) => getOnlyNumbers(val)),
    recaptcha: z.string().min(1, Mensagens.RECAPTCHA_VAZIO),
  });

  type formTypeConsultaConta = z.infer<typeof formSchemaConsultaConta>;

  async function consultarTaxa(dados: formTypeConsultaConta, resetFields: any) {
    await present({ message: Mensagens.CONSULTANDO });

    try {
      const params = new URLSearchParams({
        "g-recaptcha-response": dados.recaptcha,
      });

      const res = await axios.get(
        `${getBaseURL(ApiRoutes.ECO_ARRECADACAO_EXTERNO)}/taxa-religacao/${
          numeroConta + "-" + digitoConta
        }`,
        { params: params }
      );

      if (res?.data?.valorReligacaoNormal) {
        setListaTaxa([
          {
            label: "Normal",
            value: "NORMAL",
            detalhes: `A taxa de religação NORMAL é de ${formatarDinheiro(
              res.data.valorReligacaoNormal
            )} e o prazo para execução é de até 24(vinte e quatro) horas.`,
          },
          {
            label: "De urgência",
            value: "URGENCIA",
            detalhes:
              `A taxa de religação DE URGÊNCIA é de ${formatarDinheiro(
                res.data.valorReligacaoUrgente
              )} e o prazo para execução é de até 6(seis) horas para dias úteis no horário comercial ` +
              "e de até 14(quatorze) horas para feriados, finais de semana e solicitações fora do horário comercial.",
          },
        ]);

        setIsFluxoCadastro(true);
      } else {
        await notification.showWarning({
          message: "Não foi encontrado o valor da taxa.",
        });
      }

      dismiss();
    } catch (e: any) {
      dismiss();

      let msg = "";

      if (e.response.status === 401) {
        msg = Mensagens.RECAPTCHA_ERRO;
      }

      await notification.showError({ message: msg, exception: e });
    } finally {
      reCaptchaRef?.current?.clear();
    }
  }

  const formSchema = z
    .object({
      tipoReligacao: z.string().min(1, "Selecione o tipo de religação."),
      nomeContato: z.string().min(1, "Informe o nome do interessado."),
      cpfCnpj: z.string(),
      tipoCliente: z.any().transform((input) => input?.value),
      celular: z
        .string()
        .min(11, "Informe um número de contato.")
        .transform((val) => Number(getOnlyNumbers(val))),
      recaptcha: z.string().min(1, Mensagens.RECAPTCHA_VAZIO),
    })
    .refine(
      (data) =>
        data.cpfCnpj
          ? data.tipoCliente === TipoPessoa.PESSOA_FISICA
            ? isCpfValido(data.cpfCnpj)
            : isCnpjValido(data.cpfCnpj)
          : true,
      (data) => ({
        message:
          data.tipoCliente === TipoPessoa.PESSOA_FISICA
            ? "CPF inválido."
            : "CNPJ inválido.",
        path: ["cpfCnpj"],
      })
    );

  type formType = z.infer<typeof formSchema>;

  async function enviarDados(dados: formType, resetFields: any) {
    await present({ message: Mensagens.SALVANDO });

    try {
      let body = {
        dadosProtocolo: {
          dddTelefoneContato: getNumeroDDD(dados.celular),
          numeroTelefoneContato: getNumeroSemDDD(dados.celular),
          codigoMotivoContato: MotivoContato.RELIGACAO_DE_AGUA,
          matriculaResponsavel: getMatriculaResponsavelProtocolo(),
          descricaoAdicional: getDescricaoAdicionalProtocolo(),
          nomeContato: dados.nomeContato,
          numeroCpfCnpj: dados.cpfCnpj ? getOnlyNumbers(dados.cpfCnpj) : null,
          tipoCliente: dados.cpfCnpj ? dados.tipoCliente : "",
        },

        matriculaSolicitante: getMatriculaSolicitante(),
        numeroContaComDigito: numeroConta + "-" + digitoConta,
        prazoReligacao: dados.tipoReligacao,
      };

      let url =
        getBaseURL(ApiRoutes.ECO_ARRECADACAO_EXTERNO) + `/solicitar-religacao`;

      const params = new URLSearchParams({
        "g-recaptcha-response": dados.recaptcha,
      });

      const resp = await axios.post(url, JSON.stringify(body), {
        params: params,
        headers: {
          "content-type": "application/json",
        },
      });

      notification.showProtocolo(resp.data).then(() => {
        resetFields();
      });

      dismiss();
    } catch (e: any) {
      dismiss();

      let msg = "";

      if (e.response.status === 401) {
        msg = Mensagens.RECAPTCHA_ERRO;
      }

      await notification.showError({ message: msg, exception: e });
    } finally {
      reCaptchaRef?.current?.clear();
    }
  }

  return (
    <Page
      title="Solicitar religação de água"
      buttonType="back"
      showCardConta={false}
    >
      <div className="flex flex-col space-y-4">
        <Accordion title="Observações" open={true}>
          <DivFaltaAgua />
        </Accordion>

        <div className="card bg-opacity-70">
          <div className="card-title">Dados da ocorrência</div>

          <div className="items-center">
            <div className="flex flex-col space-y-4">
              <ReactForm
                schema={formSchemaConsultaConta}
                onError={(error, e) => {}}
                onSubmit={(data, resetFields) =>
                  consultarTaxa(data as formTypeConsultaConta, resetFields)
                }
              >
                <div className="flex flex-col space-y-4">
                  <div className="grid grid-cols-2 gap-4 xs:grid-cols-1">
                    <InputText
                      name="numeroConta"
                      label="Número da conta"
                      onInput={(e) => handleChangeNumeroConta(e)}
                      value={numeroConta}
                      inputProps={{ maxLength: 8 }}
                      disabled={isFluxoCadastro}
                    />

                    <InputText
                      name="digitoConta"
                      label="Dígito da conta"
                      onInput={(e) => handleChangeDigitoConta(e)}
                      value={digitoConta}
                      inputProps={{ maxLength: 1 }}
                      disabled={isFluxoCadastro}
                    />
                  </div>

                  <div hidden={isFluxoCadastro}>
                    <CardReCaptcha name="recaptcha" ref={reCaptchaRef} />
                  </div>

                  <div
                    className="grid grid-cols-2 gap-2 xs:grid-cols-1"
                    hidden={isFluxoCadastro}
                  >
                    <SubmitButton label="Consultar" />
                    <ButtonBack label="Voltar" />
                  </div>
                </div>
              </ReactForm>

              <ReactForm
                schema={formSchema}
                onError={(error, e) => {}}
                onSubmit={(data, resetFields) =>
                  enviarDados(data as formType, resetFields)
                }
              >
                <div
                  className="flex flex-col space-y-4"
                  hidden={!isFluxoCadastro}
                >
                  <InputComplete
                    showDivDetalhes
                    name="tipoReligacao"
                    placeHolder="Tipo de religação"
                    items={listaTaxa}
                  />

                  <InputText
                    name="nomeContato"
                    label="Nome do interessado"
                    inputProps={{ maxLength: 70 }}
                  />

                  <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-3 gap-4">
                    <SelectField
                      name="tipoCliente"
                      placeHolder="Tipo de pessoa"
                      defaultValuePosition={0}
                      onSelect={(item) => {
                        setTipoCliente(item?.value as string);
                      }}
                      items={TIPOS_PESSOA}
                    />

                    <InputText
                      name="cpfCnpj"
                      label={
                        isTipoClientePessoaFisica
                          ? "CPF do interessado"
                          : "CNPJ do interessado"
                      }
                      mask={
                        isTipoClientePessoaFisica
                          ? "999.999.999-99"
                          : "99.999.999/9999-99"
                      }
                    />

                    <InputText
                      name="celular"
                      label="Celular"
                      mask="99 99999-9999"
                    />
                  </div>

                  <CardReCaptcha name="recaptcha" ref={reCaptchaRef} />

                  <div className="grid grid-cols-2 gap-2 xs:grid-cols-1">
                    <SubmitButton label="Enviar" />
                    <ButtonBack
                      label="Voltar"
                      onClickEvent={() => setIsFluxoCadastro(false)}
                    />
                  </div>
                </div>
              </ReactForm>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
